/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useDirectOrder } from "../useDirectOrder";
import { setMembershipCallback } from "../../utils/membershipCallback";
import { CART_LINE_TYPES, ROUTES, RECURRING_PROFILE_STATUS } from "../../constants/constants";
import usePluginUtils from "../usePluginUtils";
import { useAuth } from "../useAuth";
import { useGetPurchasedMemberships } from "../../lib/react-query/useGetPurchasedMemberships";

const todayIso = () => format(new Date(), "yyyy-MM-dd");

// An active recurring profile for this membership — buying it again would
// open a parallel profile and double-charge the client.
const hasActiveRecurringFor = (purchases, membershipId) =>
  (purchases || []).some(
    (p) =>
      +p.membership_id === +membershipId &&
      p.recurring_profile_id &&
      p.recurring_profile_status === RECURRING_PROFILE_STATUS.ACTIVE
  );

export const useMembershipCheckout = () => {
  const navigate = useNavigate();
  const { setDirectOrder } = useDirectOrder();
  const { isPluginEnabled } = usePluginUtils();
  const { isAuthenticated } = useAuth();
  const selectedLocationId = useSelector((state) => state.booking.selectedLocation?.id ?? null);
  const { data: purchasedMemberships, refetch: refetchPurchasedMemberships } = useGetPurchasedMemberships(
    { on_page: 100 },
    { enabled: isAuthenticated && isPluginEnabled("membership") }
  );

  const hasActiveRecurringProfile = useCallback(
    (membershipId) => hasActiveRecurringFor(purchasedMemberships?.data, membershipId),
    [purchasedMemberships]
  );

  // Refetches first — for gates hit right after sign-in, before the cached query populates.
  const hasActiveRecurringProfileFresh = useCallback(
    async (membershipId) => {
      const { data } = await refetchPurchasedMemberships();
      return hasActiveRecurringFor(data?.data, membershipId);
    },
    [refetchPurchasedMemberships]
  );

  const checkout = useCallback(
    ({ membership, sourceServiceId, startDate }) => {
      if (!membership?.id) return;
      if (hasActiveRecurringProfile(membership.id)) return;

      setDirectOrder({
        type: CART_LINE_TYPES.MEMBERSHIP,
        entity: membership,
        params: {
          membership_id: +membership.id,
          start_date: membership.fixed_start_date || startDate || todayIso(),
        },
      });

      // Must run after setDirectOrder — that action clears the callback via middleware.
      const params = {};

      if (sourceServiceId) params.service = +sourceServiceId;
      if (isPluginEnabled("location") && selectedLocationId) params.location = +selectedLocationId;
      if (Object.keys(params).length > 0) {
        setMembershipCallback(params);
      }

      navigate(ROUTES.ORDER_SUMMARY, { state: { directOrder: true } });
    },
    [setDirectOrder, navigate, isPluginEnabled, selectedLocationId, hasActiveRecurringProfile]
  );

  return { checkout, hasActiveRecurringProfile, hasActiveRecurringProfileFresh };
};
